import React, { KeyboardEvent, useCallback } from "react";
import { X } from "lucide-react";
import ActorAvatar from "components/Actors/ActorAvatar";
import { ACTOR_AVATAR_SIZES } from "util/actorAvatarSizes";
import { formatNotificationTimestamp } from "config/dayjsConfig";
import type { Actor } from "types/actor";
import { truncatePreview, formatTemplate } from "util/previewText";
import {
  getChatInboxUiStrings,
  resolveChatInboxLocale,
  type ChatInboxStrings,
} from "i18n/chatInboxUi";

/**
 * Variant of the chat toast. `direct` is a 1:1 message, `group` is a
 * named group/team conversation, and `request` is the pending-request
 * banner shown when the recipient is the gatekeeper.
 */
export type ChatPreviewToastVariant = "direct" | "group" | "request";

/**
 * Props consumed by the realtime chat preview toast. The component is
 * intentionally presentational: it does not know about routing, sockets,
 * or modal state. The container hook (`useRealtimeToasts`) wires the
 * `onClick` callback to navigate to `/chat/:conversationPublicId` and
 * the `onDismiss` callback to `toast.dismiss(toastId)`.
 */
export interface ChatPreviewToastProps {
  /** Sender resolved into the canonical `Actor` shape used by `ActorAvatar`. */
  senderActor: Actor | null;
  /** Display name for the sender, used in titles and aria labels. */
  senderName: string;
  /** Display name of the conversation (group/team title) when available. */
  conversationTitle?: string | null;
  /** Visual variant of the toast. */
  variant: ChatPreviewToastVariant;
  /** Plain-text message body. May be empty for media-only messages. */
  bodyPreview: string;
  /** ISO-8601 timestamp of the message; rendered as a relative timestamp. */
  createdAt: string;
  /** Invoked when the user activates the toast (click, Enter, Space). */
  onClick: () => void;
  /** Invoked when the user dismisses the toast via the close button. */
  onDismiss: () => void;
  /**
   * Optional locale override. When omitted, resolves from the browser
   * via `resolveChatInboxLocale()`. Tests rely on this to render in EN,
   * JA, and KO without monkeypatching `navigator.language`.
   */
  localeStrings?: ChatInboxStrings;
}

const PREVIEW_MAX_GRAPHEMES = 120;

/**
 * Realtime in-app chat preview toast. Rendered inside the dedicated
 * top-center `<RealtimeToaster />` queue when a `chat:message` or
 * `chat:request` event arrives for a recipient who is not currently
 * viewing the matching conversation.
 *
 * Visual contract:
 * - 40px sender avatar on the left.
 * - Title row with sender name + conversation title (group/team only)
 *   and a relative timestamp on the right.
 * - One-line truncated body preview using grapheme-aware slicing so
 *   Japanese / Korean / emoji content is not split mid-character.
 * - Close button on the far right.
 *
 * Accessibility:
 * - The body region is a `role="button"` with `tabIndex={0}` and
 *   keyboard handlers for Enter and Space, with an aria-label rendered
 *   from the localized `toastAriaLabelOpenChat` template.
 * - The close button is a separate focusable target so screen readers
 *   can dismiss without activating the body.
 */
const ChatPreviewToast: React.FC<ChatPreviewToastProps> = ({
  senderActor,
  senderName,
  conversationTitle,
  variant,
  bodyPreview,
  createdAt,
  onClick,
  onDismiss,
  localeStrings,
}) => {
  const strings = localeStrings ?? getChatInboxUiStrings(resolveChatInboxLocale());

  // Compose a localized banner title based on the variant. Direct chats
  // show only the sender; group chats prefix the conversation title and
  // separator. Request banners use the request-specific template.
  const titleText = (() => {
    if (variant === "request") {
      return formatTemplate(strings.toastTitleRequest, { name: senderName });
    }
    if (variant === "group" && conversationTitle && conversationTitle.trim().length > 0) {
      return formatTemplate(strings.toastTitleGroup, {
        title: conversationTitle.trim(),
        name: senderName,
      });
    }
    return formatTemplate(strings.toastTitleDirect, { name: senderName });
  })();

  const truncatedBody = truncatePreview(bodyPreview, PREVIEW_MAX_GRAPHEMES);
  const previewLine = truncatedBody.length > 0 ? truncatedBody : strings.toastNoPreview;
  const relativeTimestamp = createdAt ? formatNotificationTimestamp(createdAt) : "";
  const ariaLabel = formatTemplate(strings.toastAriaLabelOpenChat, { name: senderName });

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>): void => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onClick();
      }
    },
    [onClick],
  );

  const handleDismissClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>): void => {
      event.stopPropagation();
      onDismiss();
    },
    [onDismiss],
  );

  return (
    <div
      className="flex w-full items-stretch overflow-hidden rounded-2xl bg-eoe-bg-modal shadow-xl ring-1 ring-black/5 dark:bg-eoe-dark-bg-modal dark:ring-white/10"
      data-testid="chat-preview-toast"
    >
      <div
        role="button"
        tabIndex={0}
        aria-label={ariaLabel}
        onClick={onClick}
        onKeyDown={handleKeyDown}
        className="flex flex-1 items-start gap-3 px-3 py-3 text-left outline-none focus-visible:ring-2 focus-visible:ring-eoe-purple cursor-pointer hover:bg-eoe-bg-hover dark:hover:bg-eoe-dark-bg-hover transition-colors"
      >
        <div className="flex-shrink-0 pt-0.5">
          {senderActor ? (
            <ActorAvatar
              actor={senderActor}
              size={ACTOR_AVATAR_SIZES.message}
              clickTo="none"
              showRing={false}
            />
          ) : (
            <div className="flex size-8 items-center justify-center rounded-full bg-eoe-bg-hover text-xs font-semibold text-eoe-text dark:bg-eoe-dark-bg-hover dark:text-eoe-dark-text">
              {senderName.slice(0, 1).toUpperCase()}
            </div>
          )}
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-baseline justify-between gap-2">
            <span className="truncate text-sm font-semibold text-eoe-text dark:text-eoe-dark-text">
              {titleText}
            </span>
            {relativeTimestamp ? (
              <span className="flex-shrink-0 text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                {relativeTimestamp}
              </span>
            ) : null}
          </div>
          <span className="mt-0.5 line-clamp-2 break-words text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
            {previewLine}
          </span>
        </div>
      </div>
      <button
        type="button"
        aria-label={strings.toastAriaLabelDismiss}
        onClick={handleDismissClick}
        className="flex flex-shrink-0 items-start px-3 py-3 text-eoe-text-sub hover:text-eoe-text dark:text-eoe-dark-text-sub dark:hover:text-eoe-dark-text outline-none focus-visible:ring-2 focus-visible:ring-eoe-purple"
      >
        <X className="size-4" aria-hidden="true" />
      </button>
    </div>
  );
};

export default ChatPreviewToast;
